import React, { Children, useId, useState } from "react";
import infoIcon from "../../../assets/brand/profile/info-18.svg";
import infoIcon20 from "../../../assets/brand/info-20.svg";
import { Btn, Card } from "./BrandUI";
import { Icon } from "./icons";
import { usePhoneTheme } from "../useIsPhone";

// A card whose body folds away (the performance cards on the coach Team ›
// Performance tab and the athlete profile; Jayson's <details open> cards).
//
//   CollapsibleCard({ title, subtitle, info: { label, onOpen }, open, defaultOpen = true,
//                     onToggle, children, id, titleAs = "h2", actions, className, bodyClassName })
//
// - The title sits in a heading (h2 unless `titleAs` says otherwise) whose
//   only child is the toggle: a <button aria-expanded aria-controls> naming
//   the card. The heading carries the V2 type class (bm-h-m, bm-h-s on
//   phones) and text-bm-ink, like every V2 card heading. A click anywhere
//   else on the header row (the chevron, the empty space) toggles too, as
//   on the mocks' <summary>.
// - `info` adds the (i) button (aria-haspopup="dialog") right after the
//   title; it is a sibling of the toggle, so it never folds the card.
//   `actions` (a badge, a select) sit at the right of the header, also
//   outside the toggle.
// - `subtitle` (the lede) and `children` live in the body, which stays
//   mounted while folded (`hidden`), so a picked season or stat survives.
// - Open by default; `open` + `onToggle(next)` make it controlled (the
//   page's Collapse all), otherwise it keeps its own state and still calls
//   onToggle. The state is never persisted.
// - Desktop: the V2 Card (12px corners, 24px padding, bm-h-m title).
//   Phones (usePhoneTheme): the phone card look (16px corners, the light
//   border, 18px side padding, bm-h-s title, bm-body-xs lede).
// - It is a <section> named by its title.
//
// useCollapseAll(ids?) is the page-level state behind one "Collapse all /
// Expand all" control: { cardProps(id), isOpen(id), setOpen(id, open),
// anyOpen, label, toggleAll(), expandAll(), collapseAll() }. Spread
// cardProps(id) on each card. When any card is open the control closes all
// of them and reads "Expand all"; otherwise it opens all and reads
// "Collapse all". Pass the ids of the cards on screen, or let cardProps
// collect them during the render (then read `anyOpen` / `label` in a child,
// as CollapseAllButton does). CollapseAllButton({ control }) is that control
// (Btn secondary sm); it renders nothing on phones (no collapse-all there).
//
// SectionDivider({ label, as = "h2", children }) is the page's section rule
// (the team page's "Game by game", "Game profile"...): the label as an
// overline heading, a hairline to the right edge, and `children` (a
// section's CollapseAllButton) after it.

const cx = (...parts) => parts.filter(Boolean).join(" ");

export const COLLAPSE_ALL = "Collapse all";
export const EXPAND_ALL = "Expand all";

// The card chrome per theme. The phone string is coachPieces.PHONE_CARD
// (and MobileCard's), repeated so this primitive does not load coach code.
const DESKTOP = { title: "bm-h-m", lede: "bm-body-m", body: "mt-4 gap-4", chevron: 18 };
const PHONE = { title: "bm-h-s", lede: "bm-body-xs", body: "mt-3 gap-3", chevron: 16 };
const PHONE_CARD = "rounded-[16px] border border-bm-mutedbg bg-white px-[18px] py-4";

const safeId = (raw) => String(raw).replace(/[^A-Za-z0-9_-]/g, "");

export const CollapsibleCard = ({
  title,
  subtitle,
  info,
  open: openProp,
  defaultOpen = true,
  onToggle,
  children,
  id,
  titleAs: Heading = "h2",
  actions,
  className = "",
  bodyClassName = "",
}) => {
  const phone = usePhoneTheme();
  const look = phone ? PHONE : DESKTOP;
  const autoId = useId();
  const baseId = id ? String(id) : `card-${safeId(autoId)}`;
  const [ownOpen, setOwnOpen] = useState(defaultOpen);
  const controlled = openProp !== undefined && openProp !== null;
  const open = controlled ? Boolean(openProp) : ownOpen;
  const titleId = `${baseId}-title`;
  const bodyId = `${baseId}-body`;

  const toggle = () => {
    const next = !open;
    if (!controlled) setOwnOpen(next);
    onToggle?.(next);
  };
  const stop = (e) => e.stopPropagation();

  const header = (
    // The row folds the card like a <summary>; the heading's button is the
    // keyboard and screen reader control, the info button and actions stop
    // their clicks here.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- the button inside is the keyboard control
    <div className="group flex cursor-pointer items-center gap-2" onClick={toggle}>
      {/* The heading carries the V2 type and colour (as SectionTitle and
          MobileCardHead do); the button inherits them (Tailwind preflight). */}
      <Heading className={cx("m-0 min-w-0 break-words text-bm-ink transition-colors group-hover:text-bm-orange", look.title)}>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          className="block max-w-full rounded-[8px] text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-bm-ink"
        >
          <span id={titleId}>{title}</span>
        </button>
      </Heading>
      {info && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            info.onOpen?.();
          }}
          aria-label={info.label}
          aria-haspopup="dialog"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full hover:bg-bm-subtle"
        >
          <img src={phone ? infoIcon20 : infoIcon} alt="" className="h-5 w-5" />
        </button>
      )}
      <span className="min-w-0 flex-1" />
      {actions && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- only keeps the actions' own clicks from folding the card
        <span className="flex shrink-0 items-center gap-2" onClick={stop}>
          {actions}
        </span>
      )}
      <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center text-bm-muted group-hover:text-bm-ink">
        <Icon name="chevron-down" size={look.chevron} className={cx("transition-transform", open ? "" : "-rotate-90")} />
      </span>
    </div>
  );

  // No display utility on the element that carries `hidden`: Tailwind's
  // `.flex` would win over the preflight's [hidden] { display: none }. A
  // card with nothing under its lede (the Player Archetype's shell before
  // any metric, notes 10/21 D4 c) draws no empty body box, so it ends where
  // its lede does.
  const body = (
    <div id={bodyId} hidden={!open}>
      {subtitle && <p className={cx("mt-[6px] break-words text-bm-muted", look.lede)}>{subtitle}</p>}
      {Children.toArray(children).length > 0 && <div className={cx("flex flex-col", look.body, bodyClassName)}>{children}</div>}
    </div>
  );

  if (phone) {
    return (
      <section aria-labelledby={titleId} data-open={open ? "true" : "false"} className={cx(PHONE_CARD, "flex min-w-0 flex-col", className)}>
        {header}
        {body}
      </section>
    );
  }
  return (
    <Card as="section" aria-labelledby={titleId} data-open={open ? "true" : "false"} className={cx("flex min-w-0 flex-col", className)}>
      {header}
      {body}
    </Card>
  );
};

const has = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

export const useCollapseAll = (ids) => {
  const [state, setState] = useState({ base: true, overrides: {} });
  // Ids seen by cardProps during this render (used when `ids` is not given).
  const seen = [];
  const isOpen = (id) => (has(state.overrides, id) ? state.overrides[id] : state.base);
  const known = () => (Array.isArray(ids) && ids.length ? ids : seen);
  const anyOpen = () => {
    const list = known();
    return list.length ? list.some(isOpen) : state.base;
  };
  const setOpen = (id, open) => setState((s) => ({ ...s, overrides: { ...s.overrides, [id]: Boolean(open) } }));
  const control = {
    isOpen,
    setOpen,
    ids: known,
    cardProps: (id) => {
      if (!seen.includes(id)) seen.push(id);
      return { id, open: isOpen(id), onToggle: (next) => setOpen(id, next) };
    },
    get anyOpen() {
      return anyOpen();
    },
    get label() {
      return anyOpen() ? COLLAPSE_ALL : EXPAND_ALL;
    },
    toggleAll: () => {
      const next = !anyOpen();
      setState({ base: next, overrides: {} });
    },
    expandAll: () => setState({ base: true, overrides: {} }),
    collapseAll: () => setState({ base: false, overrides: {} }),
  };
  return control;
};

export const CollapseAllButton = ({ control, className = "" }) => {
  const phone = usePhoneTheme();
  if (phone || !control) return null;
  const controls = control.ids().map((id) => `${id}-body`).join(" ") || undefined;
  return (
    <Btn variant="secondary" size="sm" onClick={control.toggleAll} aria-controls={controls} className={className}>
      {control.label}
    </Btn>
  );
};

export const SectionDivider = ({ label, as: Tag = "h2", children, className = "" }) => (
  <div className={cx("flex items-center gap-3", className)}>
    <Tag className="m-0 shrink-0 bm-overline">{label}</Tag>
    <span aria-hidden="true" className="h-px min-w-0 flex-1 bg-bm-border" />
    {children}
  </div>
);

export default CollapsibleCard;
